<template>
<div v-if="inputValue && spans">

  <div class="my-8">
    <h5>{{ $t('nurseryHours.extendedPatternName') }}</h5>
    <v-text-field
      v-model="inputValue.patternTitle"
      variant="outlined"
      hide-details
      density="compact"
    ></v-text-field>
  </div>

  <!-- 固定時間枠（幼稚園） -->
  <div class="menu-header">{{ $t('nurseryHours.fixedTimeSlotKindergarten') }}</div>

  <div class="my-8">
    <div>
      <h5>{{ $t('nurseryHours.startTime') }}</h5>
      <time-input
        :hour="inputValue.startingHour"
        @hour="inputValue.startingHour = Number($event)"
        :minute="inputValue.startingMinute"
        @minute="inputValue.startingMinute = Number($event)"
      ></time-input>
    </div>

    <div class="my-8">
      <billing-input
        :billingMethod="inputValue.earlyHoursBillingFee.billingMethod"
        @billingMethod="inputValue.earlyHoursBillingFee.billingMethod = $event"
        :hourly="inputValue.earlyHoursBillingFee.hourly"
        @hourly="inputValue.earlyHoursBillingFee.hourly = $event"
        :monthly="inputValue.earlyHoursBillingFee.monthly"
        @monthly="inputValue.earlyHoursBillingFee.monthly = $event"
      ></billing-input>
    </div>
  </div>

  <!-- 時間枠 -->
  <div v-for="(item, i) in spans.spans" :key="i">
    <div class="menu-header">{{ $t('nurseryHours.timeSlotN', { n: i+1 }) }}</div>

    <div class="my-8">
      <div>
        <h5>{{ $t('nurseryHours.startTime') }}</h5>
        <time-input
          :hour="item.startingHour"
          @hour="item.startingHour = Number($event)"
          :minute="item.startingMinute"
          @minute="item.startingMinute = Number($event)"
        ></time-input>
      </div>

      <div class="mt-4">
        <h5>{{ $t('nurseryHours.endTime') }}</h5>
        <time-input
          :hour="item.endingHour"
          @hour="item.endingHour = Number($event)"
          :minute="item.endingMinute"
          @minute="item.endingMinute = Number($event)"
        ></time-input>
      </div>

      <div class="my-8">
        <billing-input
          :billingMethod="item.billingMethod"
          @billingMethod="item.billingMethod = $event"
          :hourly="item.hourly"
          @hourly="item.hourly = $event"
          :monthly="item.monthly"
          @monthly="item.monthly = $event"
        ></billing-input>
      </div>
    </div>

  </div>

  <div class="d-flex">
    <v-btn
      @click="addSpanRow"
      color="primary"
      variant="outlined"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </div>

  <div class="my-12 d-flex justify-center">
    <v-btn
      @click="updatePattern"
      v-if="isUpdatable"
      :loading="isLoading"
      color="primary"
      variant="flat"
      large
    >{{ $t('common.save') }}</v-btn>
    <v-btn
      @click="createPattern"
      v-else
      :loading="isLoading"
      color="primary"
      variant="flat"
      large
    >{{ $t('common.create') }}</v-btn>
  </div>

</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { CreateSpansInput, CreateSpotKindergartenInput, nurseryHoursModule } from '@/store/dataModules/nurseryHoursModule'
import LocalDataService from '@/service/LocalDataService';
import Swal from 'sweetalert2';
import TimeInput from '@/components/organisms/nursery-hours/TimeInput.vue'
import BillingInput from '@/components/organisms/nursery-hours/BillingInput.vue';

export default defineComponent({
  name: 'SpotKindergartenPatternContainer',

  components: {
    TimeInput,
    BillingInput,
  },

  mounted() {
    if (!this.isUpdatable) {
      this.initValues();
      this.initSpans();
      this.addSpanRow();
    }
  },

  data() {
    return {
      billingMethodItems: [
        { text: this.$t('nurseryHours.perHour') as string, value: 'hourly', },
        { text: this.$t('nurseryHours.perMonth') as string, value: 'monthly', },
      ],
      isLoading: false,
    }
  },

  computed: {
    inputValue: {
      get(): CreateSpotKindergartenInput | null {
        return nurseryHoursModule.spotKindergartenPatternInput;
      },
      set(value: CreateSpotKindergartenInput) {
        nurseryHoursModule.setSpotKindergartenPatternInput(value);
      }
    },
    isUpdatable: function(): boolean {
      return (this.$route.query && this.$route.query.category != null); 
    },
    spans: {
      get(): CreateSpansInput | null {
        return nurseryHoursModule.spansInput;
      },
      set(value: CreateSpansInput) {
        nurseryHoursModule.setSpansInput(value);
      }
    },
  },

  methods: {
    initValues() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        this.inputValue = {
          nurseryId: nurseryId,
          patternTitle: this.$t('nurseryHours.defaultPatternTitle') as string,
          startingHour: 6,
          startingMinute: 0,
          earlyHoursBillingFee: {
            billingMethod: 'hourly',
            hourly: {
              billingMinute: 30,
              billingAmount: 100,
              billingUpperLimitAmount: undefined,
            },
            monthly: {
              billingAmount: 100,
            }
          },
        };
      }
    },
    initSpans() {
      this.spans = {
        patternId: '0',
        spans: [],
        spanCategory: 'normal',
      }
    },
    async createPattern() {
      this.isLoading = true;
      const response = await nurseryHoursModule.createSpotKindergartenPattern()
      .catch((e) => {
        alert(this.$t('ui.patternConfigFailed'));
      });
      if (response) {
        if (response.patternId) {
          await this.createSpans(response.patternId);
        }
        Swal.fire({
          text: this.$t('nurseryHours.createSuccess') as string,
        });
        this.$router.push({ path: '/nursery-hours' });
      }
      this.isLoading = false;
    },
    async updatePattern() {
      this.isLoading = true;
      const patternId = this.$route.query?.patternId;
      if (patternId) {
        const response = await nurseryHoursModule.updateSpotKindergartenPattern({
          patternId: String(patternId),
        })
        .catch((e) => {
          alert(this.$t('ui.patternConfigFailed'));
        });
        if (response) {
          if (response.patternId) {
            await this.updateSpans(response.patternId);
          }
          Swal.fire({
            title: this.$t('nurseryHours.saveSuccess') as string,
          });
        this.$router.push({ path: '/nursery-hours' });
        }
      }
      this.isLoading = false;
    },
    async createSpans(patternId: string) {
      if (patternId && nurseryHoursModule.spansInput) {
        nurseryHoursModule.spansInput.patternId = patternId;
        await nurseryHoursModule.saveSpans()
        .catch((e) => {
          alert(this.$t('ui.invalidTimeFrame'));
        });
      }
    },
    async updateSpans(patternId: string) {
      if (patternId && nurseryHoursModule.spansInput) {
        nurseryHoursModule.spansInput.patternId = patternId;
        await nurseryHoursModule.updateSpans()
        .catch((e) => {
          alert(this.$t('ui.invalidTimeFrame'));
        });
      }
    },
    addSpanRow() {
      this.spans?.spans.push({
        startingHour: 6,
        startingMinute: 0,
        endingHour: 6,
        endingMinute: 59,
        billingMethod: 'hourly',
        hourly: {
          billingMinute: 30,
          billingAmount: 100,
          billingUpperLimitAmount: 1000,
        },
        monthly: {
          billingAmount: 100,
        }
      });
    },
  },
})
</script>